import { styled } from '@mui/material/styles';
import {
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { theme } from 'config/theme';
import CloseLineIcon from 'remixicon-react/CloseLineIcon';
import ArrowLeftIcon from 'remixicon-react/ArrowLeftLineIcon';
import { useProjectRetroContext } from '../../context';
import { ArticleSourceType, ArticleType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { ConfigEntryType } from 'config/config';
import { ChangeEvent, useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { PostFormData } from '@buildwise/pages/addPost/hooks/useAddPostForm';
import PostDetailsForm from 'components/modals/addPostModal/PostDetailsForm';
import { CONFIGS, useConfigQuery } from '@buildwise/libs/configurations';
import useUserData from 'hooks/useUserData';
import RenderRetrospective from './RenderRetrospective';

type AddLessonFormProps = {
  article?: ArticleType;
  articleFields: ConfigEntryType[];
  articleFieldsLoading: boolean;
  loadingFiles: boolean;
  onDrop: (files: File[]) => void;
  onSubmit: () => Promise<void>;
};

function AddLessonForm({
  article,
  articleFields,
  articleFieldsLoading,
  loadingFiles,
  onDrop,
  onSubmit,
}: AddLessonFormProps) {
  const {
    state: { selectedArticle, isFullScreen, showSelectMilestone },
    dispatch,
  } = useProjectRetroContext();
  const {
    control,
    setValue,
    formState: { isSubmitting },
  } = useFormContext<PostFormData>();
  const projectSpecific = useWatch({
    control,
    name: 'projectSpecific',
    defaultValue: true,
  });
  const { companyId } = useUserData();
  const { activeUniqueEntries: phasesAndMilestones } = useConfigQuery({
    companyId,
    configName: CONFIGS.RETROSPECTIVE_PHASES.configName,
  });

  const handleCheckboxChange = useCallback((_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setValue('projectSpecific', !checked);
  }, []);

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      overflow="hidden"
      height="100%"
    >
      {showSelectMilestone ? (
        <Box>
          <StickyContainer>
            <Box display="flex" alignItems="center" justifyContent="space-between" p={1}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography
                  variant="h5"
                  sx={(theme) => ({
                    color: theme.palette.custom.tuna,
                    fontSize: 16,
                    fontWeight: 700,
                    ml: 1,
                  })}
                >
                  Select Milestone
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => {
                  dispatch({ payload: { isOpenedLessonForm: false, showSelectMilestone: false } });
                }}
              >
                <CloseLineIcon color={theme.palette.custom.paleSky} />
              </IconButton>
            </Box>
          </StickyContainer>

          <Divider />
          <Box sx={{ ml: 1, mt: 1 }}>
            {phasesAndMilestones.map((entry, index) => (
              <RenderRetrospective
                key={entry.id}
                phase={entry}
                phaseIndex={index}
                showCategoryTree={false}
              />
            ))}
          </Box>
        </Box>
      ) : (
        <>
          <StickyContainer>
            <Box display="flex" alignItems="center" justifyContent="space-between" p={1}>
              <Box display="flex" alignItems="center" gap={0.5}>
                {!selectedArticle && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      dispatch({ payload: { showSelectMilestone: true } });
                    }}
                  >
                    <ArrowLeftIcon color={theme.palette.custom.paleSky} />
                  </IconButton>
                )}

                <Typography
                  variant="h5"
                  sx={(theme) => ({
                    color: theme.palette.custom.tuna,
                    fontSize: 16,
                    fontWeight: 700,
                    ml: selectedArticle ? 1 : 1,
                  })}
                >
                  {selectedArticle ? 'Edit Learning' : 'Add New Lesson'}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => {
                  dispatch({ payload: { isOpenedLessonForm: false } });
                }}
              >
                <CloseLineIcon color={theme.palette.custom.paleSky} />
              </IconButton>
            </Box>
            <Divider />
          </StickyContainer>
          <Box p={2} height="100%" overflow="auto">
            <PostDetailsForm
              articleType={ArticleSourceType.LESSON}
              isEditing={!!article}
              isFullScreen={isFullScreen}
              loadingFiles={loadingFiles}
              onDrop={onDrop}
              articleFields={articleFields}
              configName={CONFIGS.RETROSPECTIVE_INPUT_FIELDS.configName}
              getFieldWidth={() => '100%'}
            />
          </Box>
          <Box>
            <Divider />
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} p={2}>
              <FormControlLabel
                control={<Checkbox onChange={handleCheckboxChange} disabled={isSubmitting} />}
                label="Show on Feed"
                checked={!projectSpecific}
              />
              <Button variant="contained" onClick={onSubmit} disabled={isSubmitting}>
                {isSubmitting ? (
                  <CircularProgress
                    sx={(theme) => ({ color: theme.palette.custom.black })}
                    size={20}
                  />
                ) : (
                  'Save'
                )}
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}

const StickyContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.custom.white,
  position: 'sticky',
  top: 0,
  zIndex: 1,
}));

export default AddLessonForm;
